import React, { useState } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";


function Nature3DDemo() {
    const items = [
      {
        name: "Flower",
        description: "Flowers make seeds to grow new plants!",
        fact: "Bees help flowers by pollinating them."
      },
      {
        name: "Tree",
        description: "Trees are tall plants with a woody trunk!",
        fact: "They provide oxygen and shelter for animals."
      },
      {
        name: "Bee",
        description: "Bees are insects that collect nectar!",
        fact: "They help pollinate many flowers and crops."
      },
    ];
  
    const [index, setIndex] = useState(0);
  
    const nextItem = () => {
      setIndex((prev) => (prev + 1) % items.length);
    };
  
    return (
      <div className="g1sci-nature-demo-container">
        <h4>Nature Demo: {items[index].name}</h4>
        <p className="g1sci-demo-description">{items[index].description}</p>
        <p className="g1sci-demo-fact"><strong>Fun Fact:</strong> {items[index].fact}</p>
        <button className="button1" onClick={nextItem}>
          See Another
        </button>
  
        <div style={{ width: "600px", height: "500px", margin: "-5px auto"}}>
          <Canvas camera={{ position: [0, 2, 5], fov: 50 }}>
            <OrbitControls enablePan={false} />
            <ambientLight intensity={0.5} />
            <directionalLight position={[5, 5, 5]} />
            {/* Ground plane */}
            <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -1, 0]}>
              <planeGeometry args={[10, 10]} />
              <meshStandardMaterial color="#ddd" />
            </mesh>
            {/* 3D shape: Flower, Tree, or Bee */}
            <NatureShape name={items[index].name} />
          </Canvas>
        </div>
      </div>
    );
  }
  
  /** 
   * NatureShape:
   * Renders a simple 3D model for "Flower", "Tree", or "Bee."
   */
  function NatureShape({ name }) {
    if (name === "Flower") {
      return (
        <group>
          {/* Stem: a green cylinder */}
          <mesh>
            <cylinderGeometry args={[0.05, 0.05, 1, 16]} />
            <meshStandardMaterial color="green" />
          </mesh>
          {/* Flower petals: a ring of pink boxes around top */}
          {[...Array(6)].map((_, i) => {
            const angle = (i * 2 * Math.PI) / 6;
            return (
              <mesh key={i} position={[0.15 * Math.cos(angle), 0.5, 0.15 * Math.sin(angle)]}>
                <boxGeometry args={[0.1, 0.1, 0.1]} />
                <meshStandardMaterial color="#ffb6c1" />
              </mesh>
            );
          })}
          {/* Flower center: small yellow sphere on top */}
          <mesh position={[0, 0.5, 0]}>
            <sphereGeometry args={[0.08, 16, 16]} />
            <meshStandardMaterial color="#ffeb3b" />
          </mesh>
        </group>
      );
    }
    else if (name === "Tree") {
      return (
        <group>
          {/* Trunk: brown cylinder */}
          <mesh>
            <cylinderGeometry args={[0.1, 0.1, 1.5, 16]} />
            <meshStandardMaterial color="#8B4513" />
          </mesh>
          {/* Leaves: a big green sphere on top */}
          <mesh position={[0, 1, 0]}>
            <sphereGeometry args={[0.5, 16, 16]} />
            <meshStandardMaterial color="green" />
          </mesh>
        </group>
      );
    }
    else if (name === "Bee") {
      return (
        <group>
          {/* Bee body: a small elongated sphere */}
          <mesh>
            <sphereGeometry args={[0.2, 16, 16]} />
            <meshStandardMaterial color="#fdd835" />
          </mesh>
          {/* Black stripes: boxes or small cylinders around the body */}
          <mesh position={[0, 0, 0.05]}>
            <boxGeometry args={[0.4, 0.2, 0.05]} />
            <meshStandardMaterial color="black" />
          </mesh>
          <mesh position={[0, 0, -0.05]}>
            <boxGeometry args={[0.4, 0.2, 0.05]} />
            <meshStandardMaterial color="black" />
          </mesh>
          {/* Wings: transparent white planes on top */}
          <mesh position={[0.15, 0.1, 0]} rotation={[0, 0, 0.3]}>
            <planeGeometry args={[0.3, 0.15]} />
            <meshStandardMaterial color="white" transparent opacity={0.7} />
          </mesh>
          <mesh position={[-0.15, 0.1, 0]} rotation={[0, 0, -0.3]}>
            <planeGeometry args={[0.3, 0.15]} />
            <meshStandardMaterial color="white" transparent opacity={0.7} />
          </mesh>
        </group>
      );
    }
    return null;
  }
  
  // Example visualization: very basic, but can be replaced with better models
  function SenseShape({ name }) {
    if (name === "Eye") {
      return (
        <group>
          <mesh>
            <sphereGeometry args={[0.5, 32, 32]} />
            <meshStandardMaterial color="white" />
          </mesh>
          <mesh position={[0, 0, 0.45]}>
            <sphereGeometry args={[0.2, 16, 16]} />
            <meshStandardMaterial color="blue" />
          </mesh>
        </group>
      );
    }
    if (name === "Ear") {
      return (
        <mesh rotation={[0, Math.PI / 2, 0]}>
          <torusGeometry args={[0.35, 0.1, 16, 100]} />
          <meshStandardMaterial color="#FFD700" />
        </mesh>
      );
    }
    if (name === "Nose") {
      return (
        <mesh position={[0, 0, 0]}>
          <coneGeometry args={[0.2, 0.6, 16]} />
          <meshStandardMaterial color="#fddbb0" />
        </mesh>
      );
    }
    if (name === "Tongue") {
      return (
        <mesh>
          <cylinderGeometry args={[0.2, 0.18, 0.4, 32]} />
          <meshStandardMaterial color="#e06666" />
        </mesh>
      );
    }
    if (name === "Skin") {
      return (
        <mesh>
          <boxGeometry args={[1, 1.2, 0.1]} />
          <meshStandardMaterial color="#ffe0bd" />
        </mesh>
      );
    }
    return null;
  }
  
  /* === Senses Demo === */
function SensesDemo3D() {
    const items = [
      { name: "Eye", description: "Eyes help us see.", fact: "You can see millions of colors." },
      { name: "Ear", description: "Ears help us hear.", fact: "Sound travels in waves." },
      { name: "Nose", description: "Noses help us smell.", fact: "Your nose detects thousands of scents." },
      { name: "Tongue", description: "Tongues help us taste.", fact: "Taste buds detect five basic tastes." },
      { name: "Skin", description: "Skin lets us feel touch.", fact: "Skin is your largest organ." }
    ];
    const [index, setIndex] = useState(0);
    const nextItem = () => setIndex((i) => (i + 1) % items.length);
    return (
      <div className="science-demo-container">
        <h4>{items[index].name}</h4>
        <p>{items[index].description}</p>
        <p><b>Fun Fact:</b> {items[index].fact}</p>
        <button className="button1" onClick={nextItem}>See Another</button>
        <div style={{ width: "500px", height: "400px", margin: "auto" }}>
          <Canvas camera={{ position: [0, 2, 4], fov: 50 }}>
            <OrbitControls enablePan={false} />
            <ambientLight intensity={0.5} />
            <directionalLight position={[5, 5, 5]} />
            <SensesShape name={items[index].name} />
          </Canvas>
        </div>
      </div>
    );
  }

function SensesShape({ name }) {
    if (name === "Eye") {
        return (
          <group>
            {/* White eyeball */}
            <mesh>
              <sphereGeometry args={[0.45, 32, 32]} />
              <meshStandardMaterial color="white" />
            </mesh>
            {/* Blue iris */}
            <mesh position={[0, 0, 0.40]}>
              <sphereGeometry args={[0.22, 32, 32]} />
              <meshStandardMaterial color="#1976d2" />
            </mesh>
            {/* Black pupil */}
            <mesh position={[0, 0, 0.50]}>
              <sphereGeometry args={[0.10, 32, 32]} />
              <meshStandardMaterial color="black" />
            </mesh>
          </group>
        );
      }      
      if (name === "Ear") {
        return (
          <group>
            {/* Outer ear */}
            <mesh>
              <torusGeometry args={[0.22, 0.09, 16, 64]} />
              <meshStandardMaterial color="#FFD700" />
            </mesh>
            {/* Canal (small cone at top) */}
            <mesh position={[0, 0.16, 0]} rotation={[-Math.PI/2, 0, 0]}>
              <coneGeometry args={[0.06, 0.14, 16]} />
              <meshStandardMaterial color="#FFD700" />
            </mesh>
          </group>
        );
      }      
      if (name === "Nose") {
        return (
          <group>
            {/* Left nostril */}
            <mesh position={[-0.08, 0, 0]} rotation={[0, 0, Math.PI / 9]}>
              <coneGeometry args={[0.08, 0.17, 24]} />
              <meshStandardMaterial color="#EDC3A4" />
            </mesh>
            {/* Right nostril */}
            <mesh position={[0.08, 0, 0]} rotation={[0, 0, -Math.PI / 9]}>
              <coneGeometry args={[0.08, 0.17, 24]} />
              <meshStandardMaterial color="#EDC3A4" />
            </mesh>
            {/* Optional: bridge of nose */}
            <mesh position={[0, 0.12, 0]}>
              <boxGeometry args={[0.07, 0.11, 0.07]} />
              <meshStandardMaterial color="#EDC3A4" />
            </mesh>
          </group>
        );
      }      
      if (name === "Tongue") {
        return (
          <mesh>
            <cylinderGeometry args={[0.20, 0.20, 0.20, 32]} />
            <meshStandardMaterial color="#FF6F61" />
          </mesh>
        );
      }      
      if (name === "Skin") {
        return (
          <group>
            {/* Skin patch */}
            <mesh>
              <boxGeometry args={[0.9, 0.13, 0.65]} />
              <meshStandardMaterial color="#F9DDC7" />
            </mesh>
            {/* Some bumps to symbolize texture */}
            {[...Array(4)].map((_, i) => (
              <mesh
                key={i}
                position={[-0.32 + i * 0.21, 0.07, 0.22]}
              >
                <sphereGeometry args={[0.045, 16, 16]} />
                <meshStandardMaterial color="#FFD39B" />
              </mesh>
            ))}
          </group>
        );
      }      
  return null;
}

  
  /* === Habitat Demo === */
  function HabitatDemo3D() {
    const items = [
      { name: "Forest", description: "Animals and plants thrive here.", fact: "Forests cover 31% of Earth's land." },
      { name: "Desert", description: "Very dry and hot habitat.", fact: "Cacti store water to survive." },
      { name: "Pond", description: "Small water habitat.", fact: "Frogs and lily pads love ponds." },
      { name: "Ocean", description: "Vast saltwater habitat.", fact: "Oceans cover 70% of Earth." }
    ];
    const [index, setIndex] = useState(0);
    const nextItem = () => setIndex((i) => (i + 1) % items.length);
    return (
      <div className="science-demo-container">
        <h4>{items[index].name} Habitat</h4>
        <p>{items[index].description}</p>
        <p><b>Fun Fact:</b> {items[index].fact}</p>
        <button className="button1" onClick={nextItem}>See Another</button>
        <div style={{ width: "500px", height: "400px", margin: "auto" }}>
          <Canvas camera={{ position: [0, 2, 4], fov: 50 }}>
            <OrbitControls enablePan={false} />
            <ambientLight intensity={0.5} />
            <directionalLight position={[5, 5, 5]} />
            <HabitatShape name={items[index].name} />
          </Canvas>
        </div>
      </div>
    );
  }
function HabitatShape({ name }) {
  if (name === "Forest") {
    return (
      <group>
        {/* Ground */}
        <mesh position={[0, -0.85, 0]}>
          <boxGeometry args={[2, 0.1, 2]} />
          <meshStandardMaterial color="#a3876a" />
        </mesh>
        {/* Trunk */}
        <mesh>
          <cylinderGeometry args={[0.17, 0.18, 1.5, 16]} />
          <meshStandardMaterial color="#8B4513" />
        </mesh>
        {/* Tree top */}
        <mesh position={[0, 1, 0]}>
          <sphereGeometry args={[0.7, 16, 16]} />
          <meshStandardMaterial color="#228B22" />
        </mesh>
        {/* Extra tree */}
        <mesh position={[-0.8, 0.5, -0.5]}>
          <cylinderGeometry args={[0.1, 0.1, 0.9, 12]} />
          <meshStandardMaterial color="#795548" />
        </mesh>
        <mesh position={[-0.8, 1, -0.5]}>
          <sphereGeometry args={[0.4, 12, 12]} />
          <meshStandardMaterial color="#47ae3f" />
        </mesh>
      </group>
    );
  }
  if (name === "Desert") {
    return (
      <group>
        {/* Sand */}
        <mesh position={[0, -0.5, 0]}>
          <boxGeometry args={[1.4, 0.1, 1.4]} />
          <meshStandardMaterial color="#f7dc8c" />
        </mesh>
        {/* Cactus main body */}
        <mesh>
          <cylinderGeometry args={[0.13, 0.13, 0.7, 16]} />
          <meshStandardMaterial color="#228B22" />
        </mesh>
        {/* Cactus left arm */}
        <mesh position={[-0.15, 0.15, 0]} rotation={[0, 0, Math.PI/3]}>
          <cylinderGeometry args={[0.07, 0.07, 0.25, 16]} />
          <meshStandardMaterial color="#228B22" />
        </mesh>
        {/* Cactus right arm */}
        <mesh position={[0.15, 0.25, 0]} rotation={[0, 0, -Math.PI/3]}>
          <cylinderGeometry args={[0.07, 0.07, 0.22, 16]} />
          <meshStandardMaterial color="#228B22" />
        </mesh>
        {/* Rock */}
        <mesh position={[0.45, -0.04, 0.32]}>
          <sphereGeometry args={[0.07, 10, 10]} />
          <meshStandardMaterial color="#b0b0b0" />
        </mesh>
      </group>
    );
  }
  if (name === "Pond") {
    return (
      <group>
        {/* Grassland base */}
        <mesh position={[0, -0.15, 0]}>
          <boxGeometry args={[1.5, 0.08, 1.2]} />
          <meshStandardMaterial color="#71c671" />
        </mesh>
        {/* Water (pond) */}
        <mesh position={[0, -0.10, 0]}>
          <cylinderGeometry args={[0.45, 0.5, 0.13, 32]} />
          <meshStandardMaterial color="#61dafb" />
        </mesh>
        {/* Lily pad */}
        <mesh position={[0.18, -0.05, 0.16]}>
          <cylinderGeometry args={[0.12, 0.13, 0.03, 12]} />
          <meshStandardMaterial color="#47ae3f" />
        </mesh>
        {/* Frog (just a green ball) */}
        <mesh position={[0.18, 0.03, 0.16]}>
          <sphereGeometry args={[0.04, 10, 10]} />
          <meshStandardMaterial color="#228B22" />
        </mesh>
      </group>
    );
  }
  return null;
}

  
  /* === Plant Life Cycle Demo === */
  function PlantLifeCycle3D() {
    const items = [
      { name: "Seed", description: "Plants start as seeds.", fact: "Seeds can survive for years before sprouting." },
      { name: "Sprout", description: "Seeds grow into sprouts.", fact: "Sprouts seek sunlight to grow." },
      { name: "Plant", description: "Sprouts grow into full plants.", fact: "Plants use sunlight for energy." },
      { name: "Flower", description: "Mature plants may flower.", fact: "Flowers attract pollinators." },
      { name: "Fruit", description: "Some plants produce fruit.", fact: "Fruit holds seeds for new plants." }
    ];
    const [index, setIndex] = useState(0);
    const nextItem = () => setIndex((i) => (i + 1) % items.length);
    return (
      <div className="science-demo-container">
        <h4>{items[index].name}</h4>
        <p>{items[index].description}</p>
        <p><b>Fun Fact:</b> {items[index].fact}</p>
        <button className="button1" onClick={nextItem}>See Another</button>
        <div style={{ width: "500px", height: "400px", margin: "auto" }}>
          <Canvas camera={{ position: [0, 2, 4], fov: 50 }}>
            <OrbitControls enablePan={false} />
            <ambientLight intensity={0.5} />
            <directionalLight position={[5, 5, 5]} />
            <PlantLifeCycleShape name={items[index].name} />
          </Canvas>
        </div>
      </div>
    );
  }
  function PlantLifeCycleShape({ name }) {
    if (name === "Seed") return (<mesh><sphereGeometry args={[0.2, 16, 16]} /><meshStandardMaterial color="brown" /></mesh>);
    if (name === "Sprout") return (
      <group>
        <mesh><cylinderGeometry args={[0.05, 0.05, 0.7, 16]} /><meshStandardMaterial color="green" /></mesh>
        <mesh position={[0, 0.4, 0]}><sphereGeometry args={[0.1, 16, 16]} /><meshStandardMaterial color="green" /></mesh>
      </group>
    );
    if (name === "Plant") return (
      <group>
        <mesh><cylinderGeometry args={[0.07, 0.07, 1.1, 16]} /><meshStandardMaterial color="green" /></mesh>
        <mesh position={[0, 0.7, 0]}><sphereGeometry args={[0.18, 16, 16]} /><meshStandardMaterial color="green" /></mesh>
      </group>
    );
    if (name === "Flower") return (
      <group>
        <mesh><cylinderGeometry args={[0.07, 0.07, 1.1, 16]} /><meshStandardMaterial color="green" /></mesh>
        {[...Array(6)].map((_, i) => {
          const angle = (i * 2 * Math.PI) / 6;
          return (
            <mesh key={i} position={[0.18 * Math.cos(angle), 0.9, 0.18 * Math.sin(angle)]}>
              <boxGeometry args={[0.09, 0.09, 0.09]} />
              <meshStandardMaterial color="#ffb6c1" />
            </mesh>
          );
        })}
        <mesh position={[0, 0.9, 0]}><sphereGeometry args={[0.12, 16, 16]} /><meshStandardMaterial color="#ffeb3b" /></mesh>
      </group>
    );
    if (name === "Fruit") return (<mesh><sphereGeometry args={[0.18, 16, 16]} /><meshStandardMaterial color="#d2691e" /></mesh>);
    return null;
  }
  
  /* === Forces Demo === */
  function ForcesDemo3D() {
    const items = [
      { name: "Push", description: "Push moves objects away.", fact: "You push a swing to make it go." },
      { name: "Pull", description: "Pull brings objects closer.", fact: "You pull a door to open it." },
      { name: "Gravity", description: "Gravity pulls things down.", fact: "Gravity keeps us on the ground." },
      { name: "Friction", description: "Friction slows things down.", fact: "Ice has low friction, so it's slippery." }
    ];
    const [index, setIndex] = useState(0);
    const nextItem = () => setIndex((i) => (i + 1) % items.length);
    return (
      <div className="science-demo-container">
        <h4>{items[index].name}</h4>
        <p>{items[index].description}</p>
        <p><b>Fun Fact:</b> {items[index].fact}</p>
        <button className="button1" onClick={nextItem}>See Another</button>
        <div style={{ width: "500px", height: "400px", margin: "auto" }}>
          <Canvas camera={{ position: [0, 2, 4], fov: 50 }}>
            <OrbitControls enablePan={false} />
            <ambientLight intensity={0.5} />
            <directionalLight position={[5, 5, 5]} />
            <ForcesShape name={items[index].name} />
          </Canvas>
        </div>
      </div>
    );
  }
  function ForcesShape({ name }) {
    // For demo: use arrows/boxes for push/pull, spheres for gravity, flat box for friction
    if (name === "Push") return (<mesh><boxGeometry args={[0.6, 0.2, 0.2]} /><meshStandardMaterial color="#f39c12" /></mesh>);
    if (name === "Pull") return (<mesh><boxGeometry args={[0.6, 0.2, 0.2]} /><meshStandardMaterial color="#2980b9" /></mesh>);
    if (name === "Gravity") return (<mesh><sphereGeometry args={[0.3, 32, 32]} /><meshStandardMaterial color="gray" /></mesh>);
    if (name === "Friction") return (<mesh><boxGeometry args={[1, 0.1, 1]} /><meshStandardMaterial color="#95a5a6" /></mesh>);
    return null;
  }
  
  /* === Water Cycle Demo === */
  function WaterCycleDemo3D() {
    const items = [
      { name: "Evaporation", description: "Water turns to vapor.", fact: "The sun heats water to make it evaporate." },
      { name: "Condensation", description: "Vapor forms clouds.", fact: "Clouds are made of tiny water droplets." },
      { name: "Precipitation", description: "Water falls as rain or snow.", fact: "Rain returns water to earth." },
      { name: "Collection", description: "Water gathers in lakes/rivers.", fact: "The cycle starts again!" }
    ];
    const [index, setIndex] = useState(0);
    const nextItem = () => setIndex((i) => (i + 1) % items.length);
    return (
      <div className="science-demo-container">
        <h4>{items[index].name}</h4>
        <p>{items[index].description}</p>
        <p><b>Fun Fact:</b> {items[index].fact}</p>
        <button className="button1" onClick={nextItem}>See Another</button>
        <div style={{ width: "500px", height: "400px", margin: "auto" }}>
          <Canvas camera={{ position: [0, 2, 4], fov: 50 }}>
            <OrbitControls enablePan={false} />
            <ambientLight intensity={0.5} />
            <directionalLight position={[5, 5, 5]} />
            <WaterCycleShape name={items[index].name} />
          </Canvas>
        </div>
      </div>
    );
  }
  function WaterCycleShape({ name }) {
    if (name === "Evaporation") return (<mesh><cylinderGeometry args={[0.5, 0.5, 0.2, 32]} /><meshStandardMaterial color="#61dafb" /></mesh>);
    if (name === "Condensation") return (<mesh><sphereGeometry args={[0.4, 16, 16]} /><meshStandardMaterial color="#d0f0f6" /></mesh>);
    if (name === "Precipitation") return (<mesh><cylinderGeometry args={[0.07, 0.07, 1, 16]} /><meshStandardMaterial color="#4286f4" /></mesh>);
    if (name === "Collection") return (<mesh><boxGeometry args={[1, 0.1, 1]} /><meshStandardMaterial color="#3498db" /></mesh>);
    return null;
  }
  
  /* === Energy Demo === */
  function EnergyDemo3D() {
    const items = [
      { name: "Light", description: "Energy from the sun and bulbs.", fact: "Plants need sunlight for photosynthesis." },
      { name: "Heat", description: "Energy that warms things up.", fact: "Heat can travel through solids, liquids, and gases." },
      { name: "Sound", description: "Energy we hear.", fact: "Sound moves in waves." },
      { name: "Motion", description: "Energy of moving things.", fact: "Biking uses motion energy." }
    ];
    const [index, setIndex] = useState(0);
    const nextItem = () => setIndex((i) => (i + 1) % items.length);
    return (
      <div className="science-demo-container">
        <h4>{items[index].name} Energy</h4>
        <p>{items[index].description}</p>
        <p><b>Fun Fact:</b> {items[index].fact}</p>
        <button className="button1" onClick={nextItem}>See Another</button>
        <div style={{ width: "500px", height: "400px", margin: "auto" }}>
          <Canvas camera={{ position: [0, 2, 4], fov: 50 }}>
            <OrbitControls enablePan={false} />
            <ambientLight intensity={0.5} />
            <directionalLight position={[5, 5, 5]} />
            <EnergyShape name={items[index].name} />
          </Canvas>
        </div>
      </div>
    );
  }
  function EnergyShape({ name }) {
    if (name === "Light") return (<mesh><sphereGeometry args={[0.4, 16, 16]} /><meshStandardMaterial color="yellow" /></mesh>);
    if (name === "Heat") return (<mesh><boxGeometry args={[0.8, 0.1, 0.8]} /><meshStandardMaterial color="red" /></mesh>);
    if (name === "Sound") return (<mesh><torusGeometry args={[0.6, 0.1, 16, 100]} /><meshStandardMaterial color="#0ff" /></mesh>);
    if (name === "Motion") return (<mesh><boxGeometry args={[0.7, 0.2, 0.2]} /><meshStandardMaterial color="#2ecc71" /></mesh>);
    return null;
  }
  
  /* === Body Systems Demo === */
  function BodySystemsDemo3D() {
    const items = [
      { name: "Skeleton", description: "Supports your body.", fact: "You have 206 bones!" },
      { name: "Muscles", description: "Help you move.", fact: "Muscles work in pairs." },
      { name: "Digestive System", description: "Turns food into energy.", fact: "Digestion starts in your mouth." },
      { name: "Heart", description: "Pumps blood.", fact: "Your heart beats over 100,000 times a day." }
    ];
    const [index, setIndex] = useState(0);
    const nextItem = () => setIndex((i) => (i + 1) % items.length);
    return (
      <div className="science-demo-container">
        <h4>{items[index].name}</h4>
        <p>{items[index].description}</p>
        <p><b>Fun Fact:</b> {items[index].fact}</p>
        <button className="button1" onClick={nextItem}>See Another</button>
        <div style={{ width: "500px", height: "400px", margin: "auto" }}>
          <Canvas camera={{ position: [0, 2, 4], fov: 50 }}>
            <OrbitControls enablePan={false} />
            <ambientLight intensity={0.5} />
            <directionalLight position={[5, 5, 5]} />
            <BodySystemsShape name={items[index].name} />
          </Canvas>
        </div>
      </div>
    );
  }
  function BodySystemsShape({ name }) {
    if (name === "Skeleton") return (<mesh><boxGeometry args={[0.5, 1.5, 0.1]} /><meshStandardMaterial color="#fff" /></mesh>);
    if (name === "Muscles") return (<mesh><boxGeometry args={[0.5, 1.2, 0.1]} /><meshStandardMaterial color="#e06666" /></mesh>);
    if (name === "Digestive System") return (<mesh><torusGeometry args={[0.5, 0.12, 16, 100]} /><meshStandardMaterial color="#ffb347" /></mesh>);
    if (name === "Heart") return (<mesh><sphereGeometry args={[0.2, 16, 16]} /><meshStandardMaterial color="#c0392b" /></mesh>);
    return null;
  }
  
  /* === Simple Machines Demo === */
  function SimpleMachinesDemo3D() {
    const items = [
      { name: "Lever", description: "Lifts objects easily.", fact: "A seesaw is a lever." },
      { name: "Pulley", description: "Lifts loads with ropes.", fact: "Pulleys are used in cranes." },
      { name: "Inclined Plane", description: "A sloped surface.", fact: "Ramps are inclined planes." },
      { name: "Screw", description: "Holds things together.", fact: "A screw is an inclined plane wrapped around." },
      { name: "Wedge", description: "Splits objects.", fact: "Axes and knives are wedges." }
    ];
    const [index, setIndex] = useState(0);
    const nextItem = () => setIndex((i) => (i + 1) % items.length);
    return (
      <div className="science-demo-container">
        <h4>{items[index].name}</h4>
        <p>{items[index].description}</p>
        <p><b>Fun Fact:</b> {items[index].fact}</p>
        <button className="button1" onClick={nextItem}>See Another</button>
        <div style={{ width: "500px", height: "400px", margin: "auto" }}>
          <Canvas camera={{ position: [0, 2, 4], fov: 50 }}>
            <OrbitControls enablePan={false} />
            <ambientLight intensity={0.5} />
            <directionalLight position={[5, 5, 5]} />
            <SimpleMachinesShape name={items[index].name} />
          </Canvas>
        </div>
      </div>
    );
  }
  function SimpleMachinesShape({ name }) {
    if (name === "Lever") return (<mesh><boxGeometry args={[1, 0.1, 0.1]} /><meshStandardMaterial color="#b5651d" /></mesh>);
    if (name === "Pulley") return (<mesh><torusGeometry args={[0.3, 0.08, 16, 100]} /><meshStandardMaterial color="#888" /></mesh>);
    if (name === "Inclined Plane") return (<mesh rotation={[Math.PI/6,0,0]}><boxGeometry args={[1, 0.2, 0.2]} /><meshStandardMaterial color="#a9a9a9" /></mesh>);
    if (name === "Screw") return (<mesh><cylinderGeometry args={[0.1, 0.1, 0.7, 16]} /><meshStandardMaterial color="#e0e0e0" /></mesh>);
    if (name === "Wedge") return (<mesh><coneGeometry args={[0.25, 0.7, 16]} /><meshStandardMaterial color="#d2b48c" /></mesh>);
    return null;
  }
  
  /* === Biodiversity Demo === */
  function BiodiversityDemo3D() {
    const items = [
      { name: "Mammal", description: "Warm-blooded, have hair/fur.", fact: "Whales are the largest mammals." },
      { name: "Bird", description: "Have feathers and lay eggs.", fact: "Some birds migrate thousands of kilometers." },
      { name: "Fish", description: "Live in water, have gills.", fact: "Some fish glow in the dark!" },
      { name: "Insect", description: "Six legs, exoskeleton.", fact: "Insects are the most numerous animals." },
      { name: "Plant", description: "Make their own food.", fact: "Plants produce oxygen." }
    ];
    const [index, setIndex] = useState(0);
    const nextItem = () => setIndex((i) => (i + 1) % items.length);
    return (
      <div className="science-demo-container">
        <h4>{items[index].name}</h4>
        <p>{items[index].description}</p>
        <p><b>Fun Fact:</b> {items[index].fact}</p>
        <button className="button1" onClick={nextItem}>See Another</button>
        <div style={{ width: "500px", height: "400px", margin: "auto" }}>
          <Canvas camera={{ position: [0, 2, 4], fov: 50 }}>
            <OrbitControls enablePan={false} />
            <ambientLight intensity={0.5} />
            <directionalLight position={[5, 5, 5]} />
            <BiodiversityShape name={items[index].name} />
          </Canvas>
        </div>
      </div>
    );
  }
  function BiodiversityShape({ name }) {
    if (name === "Mammal") return (<mesh><sphereGeometry args={[0.4, 16, 16]} /><meshStandardMaterial color="#d2b48c" /></mesh>);
    if (name === "Bird") return (<mesh><sphereGeometry args={[0.3, 16, 16]} /><meshStandardMaterial color="#ffe066" /></mesh>);
    if (name === "Fish") return (<mesh><boxGeometry args={[0.6, 0.2, 0.2]} /><meshStandardMaterial color="#3498db" /></mesh>);
    if (name === "Insect") return (<mesh><sphereGeometry args={[0.15, 16, 16]} /><meshStandardMaterial color="#000" /></mesh>);
    if (name === "Plant") return (<mesh><cylinderGeometry args={[0.09, 0.09, 0.6, 16]} /><meshStandardMaterial color="green" /></mesh>);
    return null;
  }
  
  /* === Electric Circuit Demo === */
  function ElectricCircuitDemo3D() {
    const items = [
      { name: "Battery", description: "Provides energy.", fact: "Batteries have a positive and negative end." },
      { name: "Wire", description: "Carries electricity.", fact: "Wires are usually made of copper." },
      { name: "Bulb", description: "Lights up with electricity.", fact: "Bulbs convert electricity into light." },
      { name: "Switch", description: "Turns circuit on/off.", fact: "Switches control the flow of electricity." }
    ];
    const [index, setIndex] = useState(0);
    const nextItem = () => setIndex((i) => (i + 1) % items.length);
    return (
      <div className="science-demo-container">
        <h4>{items[index].name}</h4>
        <p>{items[index].description}</p>
        <p><b>Fun Fact:</b> {items[index].fact}</p>
        <button className="button1" onClick={nextItem}>See Another</button>
        <div style={{ width: "500px", height: "400px", margin: "auto" }}>
          <Canvas camera={{ position: [0, 2, 4], fov: 50 }}>
            <OrbitControls enablePan={false} />
            <ambientLight intensity={0.5} />
            <directionalLight position={[5, 5, 5]} />
            <ElectricCircuitShape name={items[index].name} />
          </Canvas>
        </div>
      </div>
    );
  }
  function ElectricCircuitShape({ name }) {
    if (name === "Battery") return (<mesh><boxGeometry args={[0.4, 0.2, 0.2]} /><meshStandardMaterial color="#ffc300" /></mesh>);
    if (name === "Wire") return (<mesh><cylinderGeometry args={[0.05, 0.05, 1, 32]} /><meshStandardMaterial color="#a7a7a7" /></mesh>);
    if (name === "Bulb") return (<mesh><sphereGeometry args={[0.15, 16, 16]} /><meshStandardMaterial color="#ffeb3b" /></mesh>);
    if (name === "Switch") return (<mesh><boxGeometry args={[0.2, 0.05, 0.4]} /><meshStandardMaterial color="#616161" /></mesh>);
    return null;
  }
  
  /* === Ecosystem Demo === */
  function EcosystemDemo3D() {
    const items = [
      { name: "Producer", description: "Makes food from sunlight.", fact: "Plants are producers." },
      { name: "Consumer", description: "Eats plants or animals.", fact: "Humans and wolves are consumers." },
      { name: "Decomposer", description: "Breaks down dead things.", fact: "Mushrooms are decomposers." },
      { name: "Food Web", description: "Shows who eats whom.", fact: "Many food chains form a web." }
    ];
    const [index, setIndex] = useState(0);
    const nextItem = () => setIndex((i) => (i + 1) % items.length);
    return (
      <div className="science-demo-container">
        <h4>{items[index].name}</h4>
        <p>{items[index].description}</p>
        <p><b>Fun Fact:</b> {items[index].fact}</p>
        <button className="button1" onClick={nextItem}>See Another</button>
        <div style={{ width: "500px", height: "400px", margin: "auto" }}>
          <Canvas camera={{ position: [0, 2, 4], fov: 50 }}>
            <OrbitControls enablePan={false} />
            <ambientLight intensity={0.5} />
            <directionalLight position={[5, 5, 5]} />
            <EcosystemShape name={items[index].name} />
          </Canvas>
        </div>
      </div>
    );
  }
  function EcosystemShape({ name }) {
    if (name === "Producer") return (<mesh><cylinderGeometry args={[0.1, 0.1, 0.7, 16]} /><meshStandardMaterial color="green" /></mesh>);
    if (name === "Consumer") return (<mesh><sphereGeometry args={[0.18, 16, 16]} /><meshStandardMaterial color="#d2691e" /></mesh>);
    if (name === "Decomposer") return (<mesh><sphereGeometry args={[0.13, 16, 16]} /><meshStandardMaterial color="#888" /></mesh>);
    if (name === "Food Web") return (<mesh><torusGeometry args={[0.5, 0.12, 16, 100]} /><meshStandardMaterial color="#3e7" /></mesh>);
    return null;
  }
  
  /* === Heat Particles Demo === */
  function HeatParticlesDemo3D() {
    const items = [
      { name: "Solid", description: "Particles tightly packed.", fact: "Solids keep their shape." },
      { name: "Liquid", description: "Particles move past each other.", fact: "Liquids flow and take shape of container." },
      { name: "Gas", description: "Particles move freely.", fact: "Gases fill their container completely." },
      { name: "Heat Up", description: "Particles move faster.", fact: "Heating makes particles spread apart." }
    ];
    const [index, setIndex] = useState(0);
    const nextItem = () => setIndex((i) => (i + 1) % items.length);
    return (
      <div className="science-demo-container">
        <h4>{items[index].name}</h4>
        <p>{items[index].description}</p>
        <p><b>Fun Fact:</b> {items[index].fact}</p>
        <button className="button1" onClick={nextItem}>See Another</button>
        <div style={{ width: "500px", height: "400px", margin: "auto" }}>
          <Canvas camera={{ position: [0, 2, 4], fov: 50 }}>
            <OrbitControls enablePan={false} />
            <ambientLight intensity={0.5} />
            <directionalLight position={[5, 5, 5]} />
            <HeatParticlesShape name={items[index].name} />
          </Canvas>
        </div>
      </div>
    );
  }
  function HeatParticlesShape({ name }) {
    if (name === "Solid") return (<mesh><boxGeometry args={[0.7, 0.7, 0.7]} /><meshStandardMaterial color="#b0c4de" /></mesh>);
    if (name === "Liquid") return (<mesh><boxGeometry args={[0.7, 0.4, 0.7]} /><meshStandardMaterial color="#61dafb" /></mesh>);
    if (name === "Gas") return (<mesh><sphereGeometry args={[0.5, 16, 16]} /><meshStandardMaterial color="#f4f4f4" /></mesh>);
    if (name === "Heat Up") return (<mesh><sphereGeometry args={[0.7, 16, 16]} /><meshStandardMaterial color="#fbc02d" /></mesh>);
    return null;
  }
  
  /* === Cell Demo === */
  function CellDemo3D() {
    const items = [
      { name: "Animal Cell", description: "Round shape, nucleus in center.", fact: "Cells are the building blocks of life." },
      { name: "Plant Cell", description: "Boxy shape, has cell wall.", fact: "Plant cells have chloroplasts." },
      { name: "Nucleus", description: "Controls the cell.", fact: "Contains DNA." },
      { name: "Cell Membrane", description: "Surrounds the cell.", fact: "Controls what enters and leaves." }
    ];
    const [index, setIndex] = useState(0);
    const nextItem = () => setIndex((i) => (i + 1) % items.length);
    return (
      <div className="science-demo-container">
        <h4>{items[index].name}</h4>
        <p>{items[index].description}</p>
        <p><b>Fun Fact:</b> {items[index].fact}</p>
        <button className="button1" onClick={nextItem}>See Another</button>
        <div style={{ width: "500px", height: "400px", margin: "auto" }}>
          <Canvas camera={{ position: [0, 2, 4], fov: 50 }}>
            <OrbitControls enablePan={false} />
            <ambientLight intensity={0.5} />
            <directionalLight position={[5, 5, 5]} />
            <CellShape name={items[index].name} />
          </Canvas>
        </div>
      </div>
    );
  }
  function CellShape({ name }) {
    if (name === "Animal Cell") return (<mesh><sphereGeometry args={[0.5, 32, 32]} /><meshStandardMaterial color="#ffe4e1" /></mesh>);
    if (name === "Plant Cell") return (<mesh><boxGeometry args={[0.7, 0.7, 0.7]} /><meshStandardMaterial color="#98fb98" /></mesh>);
    if (name === "Nucleus") return (<mesh><sphereGeometry args={[0.25, 16, 16]} /><meshStandardMaterial color="#ba55d3" /></mesh>);
    if (name === "Cell Membrane") return (<mesh><sphereGeometry args={[0.55, 32, 32]} /><meshStandardMaterial color="#faebd7" wireframe /></mesh>);
    return null;
  }
  
  /* === Optics Demo === */
  function OpticsDemo3D() {
    const items = [
      { name: "Light Ray", description: "Light travels in straight lines.", fact: "A laser shows this well!" },
      { name: "Reflection", description: "Light bounces off surfaces.", fact: "Mirrors reflect light." },
      { name: "Refraction", description: "Light bends through materials.", fact: "Lenses refract light." },
      { name: "Prism", description: "Splits light into colors.", fact: "Prisms make rainbows." }
    ];
    const [index, setIndex] = useState(0);
    const nextItem = () => setIndex((i) => (i + 1) % items.length);
    return (
      <div className="science-demo-container">
        <h4>{items[index].name}</h4>
        <p>{items[index].description}</p>
        <p><b>Fun Fact:</b> {items[index].fact}</p>
        <button className="button1" onClick={nextItem}>See Another</button>
        <div style={{ width: "500px", height: "400px", margin: "auto" }}>
          <Canvas camera={{ position: [0, 2, 4], fov: 50 }}>
            <OrbitControls enablePan={false} />
            <ambientLight intensity={0.5} />
            <directionalLight position={[5, 5, 5]} />
            <OpticsShape name={items[index].name} />
          </Canvas>
        </div>
      </div>
    );
  }
  function OpticsShape({ name }) {
    if (name === "Light Ray") return (<mesh><boxGeometry args={[1, 0.05, 0.05]} /><meshStandardMaterial color="#fff700" /></mesh>);
    if (name === "Reflection") return (<mesh><boxGeometry args={[0.9, 0.05, 0.5]} /><meshStandardMaterial color="#afafaf" /></mesh>);
    if (name === "Refraction") return (<mesh><sphereGeometry args={[0.3, 16, 16]} /><meshStandardMaterial color="#8ee7f2" transparent opacity={0.7} /></mesh>);
    if (name === "Prism") return (<mesh><coneGeometry args={[0.32, 0.8, 3]} /><meshStandardMaterial color="#8e44ad" /></mesh>);
    return null;
  }
  
  /* === Genetics Demo === */
  function GeneticsDemo3D() {
    const items = [
      { name: "DNA", description: "Blueprint of life.", fact: "DNA has a double-helix shape." },
      { name: "Gene", description: "Part of DNA that codes for traits.", fact: "Genes determine hair color." },
      { name: "Chromosome", description: "Packages of DNA.", fact: "Humans have 46 chromosomes." },
      { name: "Trait", description: "A feature you inherit.", fact: "Eye color is a trait." }
    ];
    const [index, setIndex] = useState(0);
    const nextItem = () => setIndex((i) => (i + 1) % items.length);
    return (
      <div className="science-demo-container">
        <h4>{items[index].name}</h4>
        <p>{items[index].description}</p>
        <p><b>Fun Fact:</b> {items[index].fact}</p>
        <button className="button1" onClick={nextItem}>See Another</button>
        <div style={{ width: "500px", height: "400px", margin: "auto" }}>
          <Canvas camera={{ position: [0, 2, 4], fov: 50 }}>
            <OrbitControls enablePan={false} />
            <ambientLight intensity={0.5} />
            <directionalLight position={[5, 5, 5]} />
            <GeneticsShape name={items[index].name} />
          </Canvas>
        </div>
      </div>
    );
  }
  function GeneticsShape({ name }) {
    if (name === "DNA") return (<mesh><torusKnotGeometry args={[0.15, 0.04, 100, 16]} /><meshStandardMaterial color="#70a1ff" /></mesh>);
    if (name === "Gene") return (<mesh><sphereGeometry args={[0.2, 16, 16]} /><meshStandardMaterial color="#fed330" /></mesh>);
    if (name === "Chromosome") return (<mesh><boxGeometry args={[0.15, 0.6, 0.15]} /><meshStandardMaterial color="#e17055" /></mesh>);
    if (name === "Trait") return (<mesh><boxGeometry args={[0.4, 0.1, 0.2]} /><meshStandardMaterial color="#2ed573" /></mesh>);
    return null;
  }
  
  /* === Chemistry Demo === */
  function ChemistryDemo3D() {
    const items = [
      { name: "Atom", description: "Smallest unit of matter.", fact: "Atoms make up everything." },
      { name: "Molecule", description: "Group of atoms.", fact: "Water is a molecule (H2O)." },
      { name: "Chemical Change", description: "Creates something new.", fact: "Rusting is a chemical change." },
      { name: "Physical Change", description: "Changes shape or state.", fact: "Melting is a physical change." }
    ];
    const [index, setIndex] = useState(0);
    const nextItem = () => setIndex((i) => (i + 1) % items.length);
    return (
      <div className="science-demo-container">
        <h4>{items[index].name}</h4>
        <p>{items[index].description}</p>
        <p><b>Fun Fact:</b> {items[index].fact}</p>
        <button className="button1" onClick={nextItem}>See Another</button>
        <div style={{ width: "500px", height: "400px", margin: "auto" }}>
          <Canvas camera={{ position: [0, 2, 4], fov: 50 }}>
            <OrbitControls enablePan={false} />
            <ambientLight intensity={0.5} />
            <directionalLight position={[5, 5, 5]} />
            <ChemistryShape name={items[index].name} />
          </Canvas>
        </div>
      </div>
    );
  }
  function ChemistryShape({ name }) {
    if (name === "Atom") return (<mesh><sphereGeometry args={[0.15, 16, 16]} /><meshStandardMaterial color="#a55eea" /></mesh>);
    if (name === "Molecule") return (
      <group>
        <mesh position={[-0.2, 0, 0]}><sphereGeometry args={[0.15, 16, 16]} /><meshStandardMaterial color="#48dbfb" /></mesh>
        <mesh position={[0.2, 0, 0]}><sphereGeometry args={[0.15, 16, 16]} /><meshStandardMaterial color="#1dd1a1" /></mesh>
        <mesh position={[0, 0.25, 0]}><sphereGeometry args={[0.1, 16, 16]} /><meshStandardMaterial color="#feca57" /></mesh>
      </group>
    );
    if (name === "Chemical Change") return (<mesh><boxGeometry args={[0.5, 0.3, 0.2]} /><meshStandardMaterial color="#ee5253" /></mesh>);
    if (name === "Physical Change") return (<mesh><boxGeometry args={[0.5, 0.1, 0.2]} /><meshStandardMaterial color="#222f3e" /></mesh>);
    return null;
  }
  
  /* === Sustainability Demo === */
  function SustainabilityDemo3D() {
    const items = [
      { name: "Solar Power", description: "Energy from the sun.", fact: "Solar panels turn sunlight into electricity." },
      { name: "Wind Power", description: "Energy from wind.", fact: "Wind turbines generate clean energy." },
      { name: "Recycling", description: "Reusing materials.", fact: "Recycling saves resources." },
      { name: "Conservation", description: "Saving energy & nature.", fact: "Turn off lights to conserve energy!" }
    ];
    const [index, setIndex] = useState(0);
    const nextItem = () => setIndex((i) => (i + 1) % items.length);
    return (
      <div className="science-demo-container">
        <h4>{items[index].name}</h4>
        <p>{items[index].description}</p>
        <p><b>Fun Fact:</b> {items[index].fact}</p>
        <button className="button1" onClick={nextItem}>See Another</button>
        <div style={{ width: "500px", height: "400px", margin: "auto" }}>
          <Canvas camera={{ position: [0, 2, 4], fov: 50 }}>
            <OrbitControls enablePan={false} />
            <ambientLight intensity={0.5} />
            <directionalLight position={[5, 5, 5]} />
            <SustainabilityShape name={items[index].name} />
          </Canvas>
        </div>
      </div>
    );
  }
  function SustainabilityShape({ name }) {
    if (name === "Solar Power") return (<mesh><boxGeometry args={[0.7, 0.05, 0.7]} /><meshStandardMaterial color="#3498db" /></mesh>);
    if (name === "Wind Power") return (<mesh><cylinderGeometry args={[0.07, 0.07, 0.9, 16]} /><meshStandardMaterial color="#fff" /></mesh>);
    if (name === "Recycling") return (<mesh><torusGeometry args={[0.35, 0.1, 16, 100]} /><meshStandardMaterial color="#44bd32" /></mesh>);
    if (name === "Conservation") return (<mesh><boxGeometry args={[0.7, 0.1, 0.3]} /><meshStandardMaterial color="#7ed6df" /></mesh>);
    return null;
  }
  
  /* === Chemical Reactions Demo === */
  function ReactionDemo3D() {
    const items = [
      { name: "Mixing", description: "Two substances combined.", fact: "Mixing vinegar and baking soda bubbles!" },
      { name: "Color Change", description: "New color forms.", fact: "Rust is a chemical color change." },
      { name: "Gas Production", description: "Bubbles show gas.", fact: "Fizz in soda is gas escaping." },
      { name: "Temperature Change", description: "Reaction can heat/cool.", fact: "Some reactions feel warm, others cold." }
    ];
    const [index, setIndex] = useState(0);
    const nextItem = () => setIndex((i) => (i + 1) % items.length);
    return (
      <div className="science-demo-container">
        <h4>{items[index].name}</h4>
        <p>{items[index].description}</p>
        <p><b>Fun Fact:</b> {items[index].fact}</p>
        <button className="button1" onClick={nextItem}>See Another</button>
        <div style={{ width: "500px", height: "400px", margin: "auto" }}>
          <Canvas camera={{ position: [0, 2, 4], fov: 50 }}>
            <OrbitControls enablePan={false} />
            <ambientLight intensity={0.5} />
            <directionalLight position={[5, 5, 5]} />
            <ReactionShape name={items[index].name} />
          </Canvas>
        </div>
      </div>
    );
  }
  function ReactionShape({ name }) {
    if (name === "Mixing") return (<mesh><boxGeometry args={[0.6, 0.15, 0.15]} /><meshStandardMaterial color="#e17055" /></mesh>);
    if (name === "Color Change") return (<mesh><sphereGeometry args={[0.25, 16, 16]} /><meshStandardMaterial color="#9b59b6" /></mesh>);
    if (name === "Gas Production") return (<mesh><sphereGeometry args={[0.12, 16, 16]} /><meshStandardMaterial color="#fff" /><meshStandardMaterial color="#fff" transparent opacity={0.5} /></mesh>);
    if (name === "Temperature Change") return (<mesh><boxGeometry args={[0.5, 0.15, 0.2]} /><meshStandardMaterial color="#f6e58d" /></mesh>);
    return null;
  }
  
  
export { Nature3DDemo, NatureShape, SensesDemo3D, SenseShape,
    HabitatDemo3D, HabitatShape,
    PlantLifeCycle3D, PlantLifeCycleShape,
    ForcesDemo3D, ForcesShape,
    WaterCycleDemo3D, WaterCycleShape,
    EnergyDemo3D, EnergyShape,
    BodySystemsDemo3D, BodySystemsShape,
    SimpleMachinesDemo3D, SimpleMachinesShape,
    BiodiversityDemo3D, BiodiversityShape,
    ElectricCircuitDemo3D, ElectricCircuitShape,
    EcosystemDemo3D, EcosystemShape,
    HeatParticlesDemo3D, HeatParticlesShape,
    CellDemo3D, CellShape,
    OpticsDemo3D, OpticsShape,
    GeneticsDemo3D, GeneticsShape,
    ChemistryDemo3D, ChemistryShape,
    SustainabilityDemo3D, SustainabilityShape,
    ReactionDemo3D, ReactionShape, };
